import { useTranslation } from "react-i18next";
import { outsideExpected } from "../expected";
import { trend } from "../format";
import { PointIcon } from "../icon";
import { boundPoint, Frame, key, Notes, useWidgetText } from "../shared";
import type { WidgetProps } from "../types";

const ARROW = { up: "↑", down: "↓", flat: "→" } as const;

/**
 * value: one reading with its unit, age and trend (apps-and-dashboards §1.1); amber with a note when outside the
 * point's normal band (DDB-03).
 */
export function Value({ widget, input, now }: WidgetProps) {
  const fmt = useWidgetText();
  const { t } = useTranslation();
  const bound = boundPoint(widget, input);
  const point = bound?.point;
  const name = widget.title ?? point?.name ?? "";
  const cfg = widget.config ?? {};
  const latest = point?.latest;
  if (!point || !latest || typeof latest.value !== "number") {
    return (
      <Frame widget={widget} state="empty" title={name} icon={<PointIcon point={point} />}>
        <p aria-label={`${name}: ${fmt.t("state.noData")}`} className="text-gray-700">
          {fmt.t("state.noData")}
        </p>
      </Frame>
    );
  }
  const decimals = typeof cfg.decimals === "number" ? cfg.decimals : point.decimals;
  const text = fmt.value(latest.value, point, decimals);
  const offline = bound.device?.state === "offline";
  const outside = outsideExpected(point, latest.value);
  const dir =
    cfg.show_trend !== false && bound.binding.datapoint
      ? trend(input.history?.[key(bound.binding.device, bound.binding.datapoint)], now, latest.value, decimals)
      : undefined;
  return (
    <Frame widget={widget} state={offline ? "offline" : "normal"} title={name} icon={<PointIcon point={point} />}>
      <p
        aria-label={`${name}: ${text}${outside ? `, ${t("widgets.outsideExpected")}` : ""}`}
        className="flex items-baseline gap-2"
        {...(outside ? { "data-expected": "outside" } : {})}
      >
        <span
          className={`text-3xl font-semibold tracking-tight tabular-nums @[16rem]:text-4xl @[28rem]:text-5xl ${outside ? "text-amber-800" : "text-gray-900"}`}
        >
          {text}
        </span>
        {dir && (
          <span data-trend={dir} aria-hidden="true" className="text-gray-700">
            {ARROW[dir]}
          </span>
        )}
      </p>
      <Notes
        items={[
          ...(outside ? [{ text: t("widgets.outsideExpected"), tone: "warn" as const }] : []),
          ...(offline ? [{ text: fmt.t("state.offline"), tone: "warn" as const }] : []),
          ...(cfg.show_age !== false ? [{ text: fmt.age(latest.at, now) }] : []),
        ]}
      />
    </Frame>
  );
}
